import { useEffect, useMemo, useRef } from 'react';
import { Button, Checkbox, Flex, Spin, Tooltip, Typography } from 'antd';
import { LuCalculator, LuDownload, LuExpand, LuTriangleAlert } from 'react-icons/lu';
import { unByKey } from 'ol/Observable';

import { useProject } from '@contexts/Project.context';
import { exportCollectionDsp } from '@utils/api';
import { useWebGLVectorLayer } from '@hooks/useWebGLVectorLayer';
import useIsSidebarOpen from '@hooks/useIsSidebarOpen';
import { createFeature } from '@utils/map/helpers';
import { COLLECTION_LAYER_ID } from '@utils/constants';
import { useCollectionStore } from '@stores/useCollectionStore';

import {
	hexToRgba,
	DEFAULT_PRESET,
} from '@routes/userRoutes/projects/singleProject/components/modifyLayers/hexagonColorFilter/matrixFilter/matrixClassification';

import useCollectionDsp from './hooks/useCollectionDsp';
import useCollectionExportDownload from './hooks/useCollectionExportDownload';
import useCollectionFilterBody from './hooks/useCollectionFilterBody';
import useHexagonClassification from './hooks/useHexagonClassification';
import useLayerVisibilityPreference from './hooks/useLayerVisibilityPreference';
import CollectionDspTooltip from './components/CollectionDspTooltip';
import CollectionDspPanel from './components/CollectionDspPanel';
import CollectionDspColorFilter from './components/colorFilter/CollectionDspColorFilter';
import { COLLECTION_SIDEBAR_IDS } from './constants';

const { Text } = Typography;

const LAYER_ID = COLLECTION_LAYER_ID.DSP;

// Same slot the collection hexagon layer uses: below the boundary (101) and the
// detection shapes (102–104), so the plot fills underlay them rather than
// masking the detections. The two never coexist — hexagons are orthophoto-only,
// plots single-image-only — so they can share the index.
const Z_INDEX = 100;

// Unclassified plots, and the whole layer before a filter is chosen: the same
// neutral dark footprint the model DSP layer draws (see the singleImage
// footprints layer's defaultStyle), minus its hover case — hover interaction
// isn't part of this layer.
const UNCLASSIFIED_FILL = 'rgba(100, 100, 100, 0.3)';
const UNCLASSIFIED_STROKE = 'rgba(100, 100, 100, 0.6)';
const BASE_STYLE = {
	'fill-color': 'rgba(0, 0, 0, 0.3)',
	'stroke-color': 'rgba(0, 0, 0, 0.8)',
	'stroke-width': 2,
};

/** hexToRgba → a WebGL-accepted "rgba(r, g, b, a)" string. */
const toRgba = (hex, alpha) => {
	const { r, g, b, a } = hexToRgba(hex, alpha);
	return `rgba(${r}, ${g}, ${b}, ${a})`;
};

/**
 * Builds the classified WebGL style: a `match` on the per-feature `matrixClass`
 * property → each class's fill (its own opacity) and a stronger stroke, with a
 * grey fallback for anything unassigned. Mirrors the collection hexagon layer's
 * style so both stay WebGL-safe (only string/number `get`s — a boolean `get`
 * breaks shader compilation).
 *
 * @param {Record<string, { id, color, opacity }>} results - per-class results
 *   from useHexagonClassification (user classes + below_minimum/unclassified)
 */
const buildClassifiedStyle = results => {
	const entries = Object.values(results);
	// OpenLayers' `match` needs at least one case pair before the fallback, and
	// WebGL rejects an empty one — fall back to plain colors while results are
	// still empty.
	if (!entries.length) {
		return {
			'fill-color': UNCLASSIFIED_FILL,
			'stroke-color': UNCLASSIFIED_STROKE,
			'stroke-width': 1,
		};
	}
	const fillPairs = entries.flatMap(r => [r.id, toRgba(r.color, r.opacity)]);
	const strokePairs = entries.flatMap(r => [r.id, toRgba(r.color, 0.95)]);
	return {
		'fill-color': ['match', ['get', 'matrixClass'], ...fillPairs, UNCLASSIFIED_FILL],
		'stroke-color': ['match', ['get', 'matrixClass'], ...strokePairs, UNCLASSIFIED_STROKE],
		'stroke-width': 1,
	};
};

/**
 * Renders the collection's digital sampling plots — one polygon per drone image
 * footprint — on the map, colored by classification class. The single-image
 * counterpart to CollectionHexagonLayer, sourced from POST /collections/{uuid}/dsp.
 *
 * Two deliberate departures from the hexagon layer:
 *
 * - **No heat mode.** Hexagon cells tile the ground uniformly, so a density ramp
 *   over raw counts is meaningful there. Plot footprints vary in area, so the
 *   same ramp would read image size as density. Plots are therefore either
 *   classified or drawn as plain footprints.
 * - **Click opens the image.** A plot *is* an image, so clicking one opens it,
 *   matching the model DSP layer. (The hexagon layer selects the cell instead,
 *   since a lattice cell has no image behind it.)
 *
 * The filter body is built from the collection's *persisted* filters + the
 * currently visible objects, so the plots agree with the detection list and only
 * re-aggregate when filters are applied or visibility changes.
 *
 * @param {object} props
 * @param {string} props.collectionUuid
 * @param {string} props.collectionName - used as the exported report's title
 * @param {Array<{key: string, savedFilters?: object}>} props.objects - collection
 *   objects with their persisted per-object filters
 * @param {object} props.savedCollectionFilters - persisted collection-level filters
 */
const CollectionDspLayer = ({
	collectionUuid,
	collectionName,
	objects,
	savedCollectionFilters,
}) => {
	const {
		mapObject,
		project,
		singleImageFeatures,
		interactionsDisabled,
		dispatch,
		setActiveSidebar,
		removeFromActiveSidebars,
	} = useProject();
	const { uuid: projectUuid } = project ?? {};

	// Active matrix/simple color-filter. Owned by the color-filter panel; read
	// here to color the plots by class.
	const classificationFilter = useCollectionStore(s => s.dspClassificationFilter);

	// Gated on the panel's visibility, not just on a filter existing — see the
	// note in CollectionHexagonLayer, which classifies the same way.
	const isColorFilterOpen = useIsSidebarOpen(COLLECTION_SIDEBAR_IDS.dspColorFilter);
	const activeClassification = isColorFilterOpen ? classificationFilter : null;

	// Persisted filters + visible objects → per-object aggregation body. The DSP
	// endpoint takes the identical map as /hexagons and /dsp/export, so all three
	// share one derivation.
	const { filterBody, hasVisibleObjects } = useCollectionFilterBody(
		savedCollectionFilters,
		objects
	);

	const { plots, isFetching, error } = useCollectionDsp(projectUuid, collectionUuid, filterBody);

	// classByCellId drives per-feature coloring; results build the match style.
	// Percentages are a share of plots, not of area: footprints overlap, so a
	// share of their summed area would be a share of an inflated total.
	const { classByCellId, results } = useHexagonClassification(plots, activeClassification, {
		percentageBasis: 'count',
	});

	const layer = useWebGLVectorLayer(mapObject, LAYER_ID, {
		name: 'Digital Sample Plots',
		zIndex: Z_INDEX,
		visible: false,
		style: BASE_STYLE,
	});

	const features = useMemo(
		() => plots.map(plot => createFeature(plot.geometry, { plotId: plot.id })).filter(Boolean),
		[plots]
	);

	// Object key → configured color, for the tooltip's per-object swatches. Read
	// from the collection config's per-object `style.color`, the same source the
	// detection shapes are styled from, so a plot's breakdown matches the colors
	// of the detections inside it.
	const colorByKey = useMemo(
		() => Object.fromEntries((objects ?? []).map(obj => [obj.key, obj.color])),
		[objects]
	);

	// Mutate the layer's existing source (clear + add) rather than swapping in a
	// new VectorSource: the WebGL renderer repaints on the source's own change
	// events, so an in-place clear reliably empties the map when there are no
	// plots — replacing the source can leave the previous features rendered.
	useEffect(() => {
		if (!layer) return;
		const source = layer.getSource();
		source.clear();
		if (features.length) source.addFeatures(features);
	}, [layer, features]);

	const hasFeatures = features.length > 0;
	const { isEnabledByUser, setEnabledByUser, isOnMap } = useLayerVisibilityPreference(
		layer,
		hasFeatures
	);

	// Switch between the classified match style and the plain footprint style.
	// setStyle recompiles the WebGL layer, so `results` is nulled out while
	// unclassified: it is a fresh object on every classification pass (even the
	// empty one), which would otherwise recompile on each plot refetch.
	const classResults = activeClassification ? results : null;
	useEffect(() => {
		if (!layer) return;
		layer.setStyle(activeClassification ? buildClassifiedStyle(classResults) : BASE_STYLE);
	}, [layer, activeClassification, classResults]);

	// Tag each feature with its class id for the classified match style. With no
	// filter, classByCellId is empty, so matrixClass is cleared and the plain
	// style applies. Only changed features are touched.
	useEffect(() => {
		if (!layer) return;
		layer.getSource().forEachFeature(feature => {
			const classId = classByCellId[feature.get('plotId')];
			if (feature.get('matrixClass') !== classId) feature.set('matrixClass', classId);
		});
	}, [layer, features, classByCellId]);

	// Read inside the long-lived OL click handler without re-registering it every
	// time these change identity.
	const singleImageFeaturesRef = useRef(singleImageFeatures);
	const interactionsDisabledRef = useRef(interactionsDisabled);
	useEffect(() => {
		singleImageFeaturesRef.current = singleImageFeatures;
		interactionsDisabledRef.current = interactionsDisabled;
	});

	// Open the plot's image on click — a plot is an image, so this is the natural
	// drill-down and matches the model DSP layer's behavior. `plotId` is the
	// `tile_name` the endpoint returns, which is the same name the image features
	// carry, so no separate lookup table is needed.
	useEffect(() => {
		if (!mapObject || !layer) return;

		const handleClick = event => {
			if (interactionsDisabledRef.current || !layer.getVisible()) return;

			const feature = mapObject.forEachFeatureAtPixel(event.pixel, f => f, {
				layerFilter: l => l === layer,
			});
			const plotId = feature?.get('plotId');
			if (!plotId) return;

			const imageFeature = singleImageFeaturesRef.current?.find(
				img => img.get('name') === plotId
			);
			if (!imageFeature) {
				console.warn(`DSP plot click: no image feature found for "${plotId}"`);
				return;
			}
			dispatch({ type: 'setSelectedSingleImage', payload: imageFeature });
		};

		const key = mapObject.on('singleclick', handleClick);
		return () => unByKey(key);
	}, [mapObject, layer, dispatch]);

	// The classification filter is transient — tied to "the DSP layer is mounted"
	// rather than a persisted preference. Without this, leaving the layer (e.g.
	// filtering detections down to zero, or switching collection) would leave a
	// stale filter in the store for the next mount. The panels go with it: a
	// sidebar left registered while its filter is cleared would come back empty,
	// with nothing to re-seed it (only `openColorFilter` does that). Runs on
	// unmount only — safe to hold the first render's setter, which closes over
	// nothing but stable dispatch.
	useEffect(() => {
		return () => {
			useCollectionStore.getState().setDspClassificationFilter(null);
			removeFromActiveSidebars(COLLECTION_SIDEBAR_IDS.dspColorFilter);
			removeFromActiveSidebars(COLLECTION_SIDEBAR_IDS.dspPanel);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Close the aggregate card while the plots are off the map — it would only
	// report zeros. Guarded: `removeFromActiveSidebars` is a fresh function on
	// every context render, so an unguarded dispatch would loop.
	const isPanelOpen = useIsSidebarOpen(COLLECTION_SIDEBAR_IDS.dspPanel);
	useEffect(() => {
		if (isOnMap || !isPanelOpen) return;
		removeFromActiveSidebars(COLLECTION_SIDEBAR_IDS.dspPanel);
	}, [isOnMap, isPanelOpen, removeFromActiveSidebars]);

	// Server-side sampling plot export — the zip the /dsp/export endpoint writes:
	// the plot summary, one GeoJSON per object, and a manifest. It reuses the
	// filter body this layer already aggregates under, so the download holds the
	// same detections the plots on the map count. The control lives here rather
	// than in the color filter because this layer is what single-image gates:
	// mounted, and the export applies; not mounted, and there are no plots.
	const { isDownloading, download } = useCollectionExportDownload();

	const handleDownload = () =>
		download({
			request: () => exportCollectionDsp(projectUuid, collectionUuid, filterBody),
			// Named after the project, plus what was exported — the detections
			// export would otherwise take the same name.
			title: `${project?.name ?? 'collection'} sample plots`,
			fileExtension: 'zip',
			errorMessage: 'Failed to export sample plots',
			emptyMessage: 'No sample plots match the current filter',
		});

	// The details panel and the classification panel are mutually exclusive plot
	// sidebars — opening one closes the other so they never overlap
	// (setActiveSidebar just appends, it doesn't replace same-parent siblings).
	const openPanel = () => {
		removeFromActiveSidebars(COLLECTION_SIDEBAR_IDS.dspColorFilter);
		setActiveSidebar({ sidebarId: COLLECTION_SIDEBAR_IDS.dspPanel, parentId: 'layerView' });
	};

	const openColorFilter = () => {
		removeFromActiveSidebars(COLLECTION_SIDEBAR_IDS.dspPanel);
		// Seed a default classification so the panel opens with coloring active
		// rather than an empty "pick a mode" state. This is the only place a
		// default is written: opening the panel is the one moment the intent is
		// real (see the note in useHexagonFilterTemplates). Existing config is
		// left alone, so reopening restores whatever the user last had.
		const { dspClassificationFilter, setDspClassificationFilter } =
			useCollectionStore.getState();
		if (!dspClassificationFilter) {
			setDspClassificationFilter(JSON.parse(JSON.stringify(DEFAULT_PRESET)));
		}
		setActiveSidebar({
			sidebarId: COLLECTION_SIDEBAR_IDS.dspColorFilter,
			parentId: 'layerView',
		});
	};

	// The row lives as long as the layer; the controls disable rather than
	// unmount, so the toggle is always there to switch back on.
	if (!layer) return null;

	let disabledReason = null;
	if (!isFetching && !hasFeatures) {
		disabledReason = hasVisibleObjects
			? 'No sample plots for the current filters'
			: 'Sample plots follow the visible detections — show at least one object';
	}

	return (
		<>
			<li>
				<Flex align="center" justify="space-between" gap={8}>
					<Flex align="center" gap={8}>
						{/* The span carries the tooltip: a disabled input fires no
						    mouse events. */}
						<Tooltip title={disabledReason}>
							<span>
								<Checkbox
									checked={isEnabledByUser}
									disabled={isFetching || !hasFeatures}
									onChange={e => setEnabledByUser(e.target.checked)}>
									Digital Sample Plots
								</Checkbox>
							</span>
						</Tooltip>
						{isFetching && <Spin size="small" aria-label="Updating sample plots" />}
						{!isFetching && error && (
							<Tooltip title="Failed to load sample plots">
								<Text type="warning" aria-label="Failed to load sample plots">
									<LuTriangleAlert />
								</Text>
							</Tooltip>
						)}
					</Flex>
					<Flex align="center" gap={2}>
						<Tooltip title="Price estimation">
							<Button
								type="text"
								size="small"
								icon={<LuCalculator />}
								onClick={openColorFilter}
								disabled={!isOnMap}
								aria-label="Open DSP price estimation"
							/>
						</Tooltip>
						<Tooltip title="DSP details">
							<Button
								type="text"
								size="small"
								icon={<LuExpand />}
								onClick={openPanel}
								disabled={!isOnMap}
								aria-label="DSP details"
							/>
						</Tooltip>
						{/* The span carries the tooltip: a disabled button fires no
						    mouse events. */}
						<Tooltip title="Download sample plots (zip)">
							<span style={{ display: 'inline-flex' }}>
								<Button
									type="text"
									size="small"
									icon={<LuDownload />}
									loading={isDownloading}
									onClick={handleDownload}
									disabled={!isOnMap}
									aria-label="Download sample plots"
								/>
							</span>
						</Tooltip>
					</Flex>
				</Flex>
			</li>

			{/* Hover tooltip carrying each plot's own area, tally and per-object
			    density. Gated on visibility so it only reacts while the plots are
			    shown. Portals its own DOM, so it renders nothing at this position. */}
			<CollectionDspTooltip
				enabled={isOnMap}
				layer={layer}
				plots={plots}
				colorByKey={colorByKey}
			/>

			{/* Whole-collection aggregate. Portals into the modify-layers sidebar;
			    renders nothing at this position until opened. */}
			<CollectionDspPanel plots={plots} />

			{/* Matrix/simple classification panel. Portals into the modify-layers
			    sidebar; writes the active filter to the store to color plots by
			    class. `results` is passed down rather than recomputed — this layer
			    already classifies the same plots/filter for map styling. */}
			<CollectionDspColorFilter
				plots={plots}
				results={results}
				collectionName={collectionName}
				collectionUuid={collectionUuid}
				objects={objects}
			/>
		</>
	);
};

export default CollectionDspLayer;
